import type { JSX } from "react";
import { useStatsFetch } from "../../api/hooks/useStatsFetch";

function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

/**
 * scenario-relationship (Phase ε 機構 M9 第 4 sub-spec) Task 9.1:
 * ScenarioLineagePanel の「関連シナリオ」 section。 同 universe 内 scenario 間の
 * declared relationship (= sequel / spinoff / reinterpret / alt-worldline) を双方向 list 表示。
 *
 * - props: `{ scenarioId }` のみ (= sibling LineageStatsSection と同 doctrine)
 * - useStatsFetch<ApiScenarioRelationshipListResponse> 経由で 1 fetch
 * - outgoing / incoming を kind 別 grouping、 各 relationship の target/source title を表示
 * - 0 件で「関連シナリオなし」 表示
 * - data-testid="scenario-lineage-relationship-section"
 *
 * 設計対応:
 * - design.md §UI ScenarioRelationshipSection
 * - requirements.md Req 5.1-5.6
 *
 * NOTE: client-player は monorepo boundary doctrine により @palimia/core-domain dep を
 * 持たないため、 ApiScenarioRelationshipListResponse の shape は client-player 側で独自宣言
 * (= sibling spawn-stats / archived-worldlines-stats と同 doctrine)。
 */

const KIND_LABEL: Record<RelationshipKind, string> = {
  sequel: "続編",
  spinoff: "スピンオフ",
  reinterpret: "別解釈版",
  "alt-worldline": "別世界線",
};

const KIND_ORDER: readonly RelationshipKind[] = [
  "sequel",
  "spinoff",
  "reinterpret",
  "alt-worldline",
] as const;

type RelationshipKind = "sequel" | "spinoff" | "reinterpret" | "alt-worldline";

interface ScenarioSummary {
  id: string;
  title: string;
  generationMode: string;
}

interface RelationshipResponse {
  id: string;
  sourceScenarioId: string;
  targetScenarioId: string;
  kind: RelationshipKind;
  orderHint: number | null;
  metadata: unknown | null;
  createdAt: string;
}

interface OutgoingBundle {
  relationship: RelationshipResponse;
  target: ScenarioSummary;
}

interface IncomingBundle {
  relationship: RelationshipResponse;
  source: ScenarioSummary;
}

interface ScenarioRelationshipListResponse {
  outgoing: OutgoingBundle[];
  incoming: IncomingBundle[];
}

export interface ScenarioRelationshipSectionProps {
  /** 当 scenario の id (= 双方向 relationship を fetch する key)。 */
  scenarioId: string;
}

export function ScenarioRelationshipSection({
  scenarioId,
}: ScenarioRelationshipSectionProps): JSX.Element {
  const state = useStatsFetch<ScenarioRelationshipListResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/relationships`,
    "関連シナリオ",
  );

  return (
    <section
      data-testid="scenario-lineage-relationship-section"
      className="flex flex-col gap-1 px-2 py-2 bg-slate-900/40 rounded"
    >
      <h3 className="text-xs text-slate-300/80 font-medium">関連シナリオ</h3>
      {state.isLoading ? (
        <p
          data-testid="scenario-lineage-relationship-loading"
          className="text-xs text-slate-200/60"
        >
          読み込み中…
        </p>
      ) : state.error !== null || state.data === null ? null : state.data.outgoing
          .length === 0 && state.data.incoming.length === 0 ? (
        <p
          data-testid="scenario-lineage-relationship-empty"
          className="text-xs text-slate-200/60"
        >
          関連シナリオなし
        </p>
      ) : (
        <div
          data-testid="scenario-lineage-relationship-list"
          className="text-xs flex flex-col gap-2 text-slate-100"
        >
          {state.data.outgoing.length > 0 ? (
            <RelationshipGroup
              direction="outgoing"
              bundles={state.data.outgoing}
            />
          ) : null}
          {state.data.incoming.length > 0 ? (
            <RelationshipGroup
              direction="incoming"
              bundles={state.data.incoming}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}

interface RelationshipGroupProps {
  direction: "outgoing" | "incoming";
  bundles: OutgoingBundle[] | IncomingBundle[];
}

function RelationshipGroup({
  direction,
  bundles,
}: RelationshipGroupProps): JSX.Element {
  // kind 別 grouping (= sequel / spinoff / reinterpret / alt-worldline 順)
  const grouped = new Map<RelationshipKind, (OutgoingBundle | IncomingBundle)[]>();
  for (const bundle of bundles) {
    const kind = bundle.relationship.kind;
    const list = grouped.get(kind) ?? [];
    list.push(bundle);
    grouped.set(kind, list);
  }

  return (
    <div
      data-testid={`scenario-lineage-relationship-${direction}`}
      className="flex flex-col gap-1"
    >
      <p className="text-xs text-slate-300/70">
        {direction === "outgoing" ? "このシナリオから" : "このシナリオへ"}
      </p>
      <ul className="flex flex-col gap-0.5">
        {KIND_ORDER.flatMap((kind) => {
          const list = grouped.get(kind);
          if (list === undefined || list.length === 0) return [];
          return list.map((bundle) => {
            const counterpart =
              direction === "outgoing"
                ? (bundle as OutgoingBundle).target
                : (bundle as IncomingBundle).source;
            return (
              <li
                key={bundle.relationship.id}
                className="flex items-center gap-2"
              >
                <span className="text-slate-300/70 min-w-[5em]">
                  {KIND_LABEL[kind]}:
                </span>
                <span className="text-slate-100">{counterpart.title}</span>
              </li>
            );
          });
        })}
      </ul>
    </div>
  );
}
